import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import { h } from 'preact'
import classnames from 'classnames'
import { useState, useEffect, useRef } from 'preact/compat'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { adyenBancontactCancel } from '../../../api/bancontact'
import { checkPaymentStatus } from '../../../api/payments'
import { config } from '../../../config'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import { paymentStatuses } from '../../../constants/paymentStatuses'
import { DataDog } from '../../../datadog'
import { RESPONSE_ERROR_TEXT } from '../../../helpers/serverTextResponses'
import { useTranslations } from '../../../hooks/useTranslations'
import './styles.scss'
import InfoMessage from '../../Common/InfoMessage'
import {
  formatAsCurrency,
  getSupportPhoneNumber,
  redirect,
  sprintf,
} from '../../../helpers/common'
import ErrorMessage from '../../Common/ErrorMessage'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import Loader from '../../Common/Loader'
import { handlePayment } from '../../../helpers/payment'
import useAuthoriseError from '../../../hooks/useAuthoriseError'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import Modal from '../../Common/Modal'
import useThrottle from '../../../hooks/useThrottle'

const ADYEN_ENVIRONMENT = {
  TEST: 'test',
  LIVE: 'live',
}

const ADYEN_STATE_RESULTS = {
  Authorised: 'Authorised',
  Cancelled: 'Cancelled',
  Error: 'Error',
  Refused: 'Refused',
}

const checkTimerLessOneMinute = expiryDate => {
  if (!expiryDate) return false

  const inputDate = new Date(expiryDate)
  const now = new Date()
  const diffMs = inputDate - now

  return diffMs >= 0 && diffMs < 60 * 1000
}

export default function Bancontact({
  handleSubmit,
  showPaymentConfirmation,
  loaderImage,
}) {
  const l10n = useTranslations()

  const [error, setError] = useState('')
  const [isPaymentPending, setIsPaymentPending] = useState(false)
  const pageState = useSelector(pageStateSelector)
  const transactionDetails = useSelector(transactionDetailsSelector)
  const [isPaymentDisabled, setPaymentDisabled] = useState(true)
  const [isPaymentStatusAuthorised, setPaymentStatusAuthorised] =
    useState(false)
  const [isPaymentStatusPending, setIsPaymentStatusPending] = useState(true)
  const isAdyen = transactionDetails?.adyenPaymentMethods?.includes(
    PAYMENT_METHOD_TYPES.Bancontact,
  )
  const dropinContainerRef = useRef(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalLoader, setModalLoader] = useState(false)
  const [modalCancelButtonDisabled, setModalCancelButtonDisabled] =
    useState(false)
  const [isExpiryDateLessOneMin, setExpiryDateLessOneMin] = useState(
    checkTimerLessOneMinute(transactionDetails?.expiryDate),
  )
  const payButtonClasses = classnames('button', 'primary', 'medium', {
    loading: isPaymentStatusPending,
  })

  const throttledCancel = useThrottle(() => {
    setModalLoader(true)
    DataDog.info('Bancontact Adyen modal close click')
    adyenBancontactCancel(transactionDetails.id)
      .then(() => {
        DataDog.info(
          `Bancontact Adyen payment canceled (clicked close button in the popup)`,
        )
      })
      .finally(() => {
        setModalLoader(false)
        setModalOpen(false)
      })
  }, 1000)

  useEffect(() => {
    if (!pageState.isPaymentBlocked) {
      setError('')
    }
  }, [pageState.isPaymentBlocked])

  const handlePaymentStatus = (isShowWarning = true) => {
    setIsPaymentStatusPending(true)

    DataDog.info('Request payment status of Bancontact')

    return checkPaymentStatus(
      isAdyen ? 'adyen/bancontact' : 'bancontact',
      transactionDetails.id,
    ).then(
      ({ responseData }) => {
        DataDog.info('Bancontact payment status response', responseData)
        if (responseData?.status === paymentStatuses.AUTHORISE_REQUESTED) {
          setPaymentDisabled(true)
          if (isShowWarning) setPaymentStatusAuthorised(true)
        } else {
          setPaymentDisabled(false)
          setPaymentStatusAuthorised(false)
        }

        setIsPaymentStatusPending(false)

        return responseData?.status
      },
    )
  }

  useEffect(async () => {
    window.onpageshow = async event => {
      if (event.persisted) {
        DataDog.info('Bancontact user went back to payment page')
        await handlePaymentStatus()
      }
    }

    await handlePaymentStatus()
  }, [])

  useFailureRedirect()
  const handleAuthoriseError = useAuthoriseError()

  const formatAmount = formatAsCurrency(
    transactionDetails.currency,
    transactionDetails.culture,
  )

  const currentAmount = formatAmount(
    transactionDetails?.overrideAmount
      ? transactionDetails.overrideAmount
      : transactionDetails.amount,
  )

  const paymentData = {
    pageState,
    setError,
    l10n,
    setIsPaymentPending,
    handleSubmit,
    transactionDetails,
    handleAuthoriseError,
    paymentMethod: PAYMENT_METHOD_TYPES.Bancontact,
  }

  const handleAdyenPayment = async () => {
    const data = await handlePayment(paymentData)

    if (!data) return

    try {
      DataDog.info('AdyenCheckout instance creation')
      const checkout = await AdyenCheckout({
        ...data,
        environment:
          config.env === 'prod' || config.env === 'pprd'
            ? ADYEN_ENVIRONMENT.LIVE
            : ADYEN_ENVIRONMENT.TEST,

        onChange: (state, component) => {
          DataDog.info('Bancontact Adyen onChange', {
            state,
            component,
          })
        },

        beforeSubmit: async (adyenPaymentData, component, actions) => {
          DataDog.info('Bancontact Adyen beforeSubmit')
          setModalCancelButtonDisabled(true)

          const paymentStatus = await handlePaymentStatus(false)

          if (
            paymentStatus === paymentStatuses.ABORT ||
            paymentStatus === paymentStatuses.ERROR
          ) {
            await actions.reject()
            setModalCancelButtonDisabled(false)
          } else {
            await actions.resolve(adyenPaymentData)
          }
        },
        onActionHandled: ({ componentType, actionDescription }) => {
          DataDog.info('Bancontact Adyen onActionHandled', {
            componentType,
            actionDescription,
          })
        },
        onPaymentCompleted: async result => {
          console.log('Payment completed:', result)
          DataDog.info(`Bancontact Adyen payment completed`, result)

          if (result.resultCode === ADYEN_STATE_RESULTS.Authorised) {
            redirect(transactionDetails?.successUrl)
          } else if (
            result.resultCode === ADYEN_STATE_RESULTS.Refused ||
            result.resultCode === ADYEN_STATE_RESULTS.Cancelled
          ) {
            adyenBancontactCancel(transactionDetails.id)
          } else {
            redirect(transactionDetails?.failureUrl)
          }

          setModalCancelButtonDisabled(false)
        },
        onPaymentFailed: er => {
          console.error('Payment error:', er)
          DataDog.warn(`Bancontact Adyen payment error`, er)

          setModalCancelButtonDisabled(false)
          redirect(transactionDetails?.failureUrl)
        },
        onError: (checkoutError, component) => {
          DataDog.warn('Bancontact Adyen onError', {
            checkoutError,
            component,
          })
        },

        paymentMethodsConfiguration: {
          bcmc: {
            name: 'Bancontact Card',
          },
          bcmc_mobile: {
            name: 'Bancontact Mobile',
            showPayButton: true,
          },
        },
      })

      try {
        setModalOpen(true)
        checkout.create('dropin').mount(dropinContainerRef.current)
        DataDog.info('Bancontact Adyen modal has opened')
      } catch (componentError) {
        setError(RESPONSE_ERROR_TEXT(l10n))
        DataDog.error(
          `Bancontact component could not be created:`,
          componentError,
        )
        console.error(
          'Bancontact component could not be created:',
          componentError,
        )
      }
    } catch (err) {
      console.error('Checkout initialization error:', err)
      DataDog.error(`Failed to initialize payment the Bancontact Adyen`, err)
    }
  }

  useEffect(() => {
    const time = setInterval(() => {
      setExpiryDateLessOneMin(
        checkTimerLessOneMinute(transactionDetails?.expiryDate),
      )
    }, 1000)

    return () => {
      clearInterval(time)
    }
  }, [])

  return (
    <div className="bancontact-wrapper">
      {showPaymentConfirmation && (
        <InfoMessage
          message={sprintf(
            l10n.t(`You're about to pay %s with Bancontact`),
            currentAmount,
          )}
          data-testid="bancontact-info-message"
          className="bancontact-info-message"
        />
      )}

      <ErrorMessage
        isShown={!!error}
        message={error}
        data-testid="bancontact-error"
      />

      <ErrorMessage
        isShown={isExpiryDateLessOneMin}
        message={l10n.t(
          'Sorry, Bancontact is currently not available, please try another payment method',
        )}
        data-testid="bancontact-timeout-error"
      />

      <ErrorMessage
        isShown={isPaymentStatusAuthorised}
        message={l10n.t(
          'Your payment request is currently being processed, if you are not redirected, please contact customer services on',
        )}
      >
        <div className="support-phone-number">
          {getSupportPhoneNumber(transactionDetails.culture)}
        </div>
      </ErrorMessage>

      {isPaymentPending ? (
        <Loader loaderImage={loaderImage} />
      ) : (
        <div className="bancontact-button">
          <button
            type="button"
            className={payButtonClasses}
            data-testid="bancontact-button"
            onClick={async () => {
              if (isAdyen) {
                await handleAdyenPayment()
              } else {
                await handlePayment(paymentData)
              }
            }}
            tabIndex="0"
            disabled={isPaymentDisabled || !!isExpiryDateLessOneMin}
          >
            {l10n.t('Pay Now')}
          </button>
        </div>
      )}

      <Modal
        className="bancontact-adyen-modal"
        withControls={false}
        isOpened={modalOpen}
        onCancel={throttledCancel}
        showIcon={!modalCancelButtonDisabled}
        onlyIcon
        isClickableOutside={!modalCancelButtonDisabled}
      >
        {modalLoader ? (
          <Loader />
        ) : (
          <div ref={dropinContainerRef} id="dropin-container"></div>
        )}
      </Modal>
    </div>
  )
}

Bancontact.propTypes = {
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
  loaderImage: PropTypes.string,
}
